import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Policies = () => {
  const policyAreas = [
    {
      title: 'Life',
      icon: '/assets/Life.png',
      tagline: 'PROTECTING EVERY LIFE',
      intro: 'Every human life is precious. I believe in the dignity of every person from conception to natural death, and I am committed to practical, compassionate laws that reflect that truth.',
      sections: [
        {
          title: 'Born Equal Care and Protection Bill',
          content: 'I am proud to have introduced the Born Equal Care and Protection Bill into the Western Australian Parliament. This Bill addresses the shocking reality that some babies survive abortion procedures but are then denied proper medical care. The Born Equal Bill does three key things:',
          points: [
            'It recognises every baby born alive during an abortion as a human person with the same right to care as any other newborn.',
            'It requires health staff to provide life-saving treatment where possible, or respectful palliative care when survival is not medically realistic.',
            'It restores proper accountability by requiring that the death of any baby born alive after an abortion is reported to the coroner.',
          ],
          conclusion: 'No baby should be left alone to die because an abortion did not go to plan. My Bill is about basic humanity: if a child is born alive, that child deserves care.'
        }
      ],
      additional: [
        'Support and protect pregnancy support centres that walk alongside women facing crisis pregnancies with material, financial and emotional help.',
        'Fight for stronger investment in palliative care and hospices, especially in regional communities, so no one has to face the end of life without proper support and pain management.',
      ]
    },
    {
      title: 'Faith',
      icon: '/assets/Faith.png',
      tagline: 'DEFENDING RELIGIOUS FREEDOM',
      intro: 'The freedoms we enjoy in Australia did not appear by accident. They grew out of Biblical principles of human dignity and responsibility. Those freedoms are now under pressure and must be defended.',
      additional: [
        'Defend freedom of religion, speech, worship, assembly and conscience.',
        'Protect the right of Christian and faith-based schools to employ staff who support their beliefs and mission.',
        'Stand up for the right of parents to raise and educate their children in line with their Christian convictions.',
        'Protect religious freedom in schools.',
        'Expand chaplaincy services in government and non-government schools.',
      ]
    },
    {
      title: 'Family',
      icon: '/assets/Family.png',
      tagline: 'STRENGTHENING FAMILIES',
      intro: 'Strong families are the foundation of a healthy society. Government should support families, not make their lives harder. I unapologetically defend the natural family and will back policies that help parents raise their children well.',
      sections: [
        {
          title: 'Family Income Splitting',
          content: 'Our tax system often punishes single-income or uneven-income families, especially when one parent steps back from paid work to care for children. Family income splitting is a fairer way to tax households. Under family income splitting, the total income of a married couple with at least one dependent child is combined and then divided equally between the two parents for tax purposes. Tax is calculated as if each parent earned half the total income. This reform would:',
          points: [
            'Reduce the tax burden on families where one parent works full-time and the other works part-time or stays home to care for children.',
            'Recognise that parents share responsibilities and costs, even when only one pay packet comes through the door.',
            'Give families greater freedom to choose the best work and care arrangements for their children.',
          ],
          conclusion: 'I will continue to advocate strongly for family income splitting as a practical, pro-family tax reform.'
        }
      ],
      additional: [
        'Uphold marriage as the union of one man and one woman.',
        'Extend funded counselling and support to whole families, not just individuals, to help prevent relationship breakdown.',
        'Support mentoring programs that strengthen fatherhood and encourage men to take responsibility in their homes and communities.',
        'Back early-intervention programs that prevent domestic violence before it occurs.',
        'Support fair, practical arrangements for both parents to have meaningful access to their children where it is safe.',
      ]
    },
    {
      title: 'Freedom',
      icon: '/assets/Freedom.png',
      tagline: 'DEFENDING OUR FREEDOMS',
      intro: 'The freedoms we enjoy in Australia did not appear by accident. They grew out of Biblical principles of human dignity and responsibility. Those freedoms are now under pressure and must be defended.',
      additional: [
        'Defend freedom of religion, speech, worship, assembly and conscience.',
        'Protect the right of Christian and faith-based schools to employ staff who support their beliefs and mission.',
        'Stand up for the right of parents to raise and educate their children in line with their Christian convictions.',
      ]
    }
  ];

  const otherPolicies = [
    {
      title: 'Economy',
      icon: '💼',
      items: [
        'Support a compassionate immigration system that protects local jobs and housing affordability',
        'Improve housing affordability through zero stamp duty for eligible first-home buyers',
        'Cut red tape and set clear deadlines for government approvals',
        'Work towards abolishing payroll tax',
        'Promote limited government and free enterprise'
      ]
    },
    {
      title: 'Education',
      icon: '📚',
      items: [
        'Protect religious freedom in schools',
        'Expand chaplaincy services in schools',
        'Support curriculum focused on foundational skills without radical ideology',
        'Back parents who choose to home-educate',
        'Establish stronger mental-health support across the education system'
      ]
    },
    {
      title: 'Health',
      icon: '🏥',
      items: [
        'Push to reduce hospital waitlists',
        'Oppose legislation that denies biological reality of male and female',
        'Champion a drug-free Australia through early intervention',
        'Support a fair and sustainable NDIS',
        'Oppose coercive vaccine mandates'
      ]
    },
    {
      title: 'Regional WA',
      icon: '🌾',
      items: [
        'Champion regional representation',
        'Fight for better access to health and education in regional WA',
        'Protect Australian farm ownership',
        'Back sustainable live-export trade'
      ]
    },
    {
      title: 'Social Policy',
      icon: '🤝',
      items: [
        'Push for stronger online protections for children',
        'Support laws to combat sexual exploitation',
        'Advocate for Special Assistance Visa for persecuted Christians',
        'Protect girls and womens sport',
        'Support a Royal Commission into Covid pandemic handling'
      ]
    },
    {
      title: 'First Nations',
      icon: '🪃',
      items: [
        'Hold governments accountable for protecting Aboriginal children',
        'Work with Aboriginal leaders on culturally appropriate strategies',
        'Back efforts to reduce Indigenous suicide rates',
        'Oppose two-tiered policing',
        'Promote reconciliation grounded in equal dignity and truth'
      ]
    },
    {
      title: 'Environment & Energy',
      icon: '🌱',
      items: [
        'Oppose Net Zero mandates that drive up power prices',
        'Support independent scientific review of environmental policy',
        'Back economically responsible and environmentally sustainable resource development',
        'Promote wise stewardship of the natural world'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 lg:px-12 bg-cream">
        <div className="container mx-auto text-center">
          <span className="text-gold text-lg md:text-xl font-bold tracking-widest uppercase">
            THESE ARE
          </span>
          <h1 className="mt-6 font-cormorant text-5xl md:text-7xl text-navy">
            My Policies
          </h1>
          <p className="mt-6 text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            As the Australian Christians Member of the Legislative Council for Western Australia, 
            I stand for life, faith, family and freedom. I entered Parliament to give a clear Christian 
            conservative voice to those values and to fight for the most vulnerable in our community.
          </p>
        </div>
      </section>

      {/* Core Policy Areas - Full Detail */}
      {policyAreas.map((policy, idx) => (
        <section
          key={policy.title}
          className={`py-20 px-6 lg:px-12 ${idx % 2 === 0 ? 'bg-white' : 'bg-cream'}`}
        >
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              {/* Icon with hover animation */}
              <div className="w-32 h-32 flex-shrink-0 mx-auto md:mx-0 icon-hover-subtle">
                <img
                  src={policy.icon}
                  alt={policy.title}
                  className="w-full h-full object-contain filter-gold"
                />
              </div>

              {/* Content */}
              <div className="flex-1">
                <span className="block text-gold text-sm md:text-base font-bold tracking-widest uppercase">
                  {policy.tagline}
                </span>
                <h2 className="mt-3 font-cormorant-garamond text-5xl md:text-6xl italic text-navy hover-text-zoom">
                  {policy.title}
                </h2>
                
                <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                  {policy.intro}
                </p>

                {/* Detailed Sections */}
                {policy.sections && policy.sections.map((section, sIdx) => (
                  <div key={sIdx} className="mt-8">
                    <h3 className="font-cormorant text-3xl text-navy mb-4">
                      {section.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {section.content}
                    </p>
                    {section.points && (
                      <ul className="mt-4 space-y-3 mb-4">
                        {section.points.map((point, pointIdx) => (
                          <li key={pointIdx} className="flex items-start">
                            <span className="text-gold mr-3 text-xl flex-shrink-0">●</span>
                            <span className="text-gray-600">{point}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.conclusion && (
                      <p className="text-gray-600 leading-relaxed italic border-l-4 border-gold pl-4 mt-4">
                        {section.conclusion}
                      </p>
                    )}
                  </div>
                ))}

                {/* Additional Commitments */}
                {policy.additional && (
                  <div className="mt-8">
                    <h4 className="font-bold text-navy mb-4 text-lg">
                      {policy.sections ? 'Beyond this, I will:' : 'I will:'}
                    </h4>
                    <ul className="space-y-3">
                      {policy.additional.map((item, itemIdx) => (
                        <li key={itemIdx} className="flex items-start">
                          <span className="text-gold mr-3 text-xl flex-shrink-0">●</span>
                          <span className="text-gray-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Other Policy Areas with hover animations */}
      <section className="py-20 px-6 lg:px-12 bg-navy">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-gold text-lg md:text-xl font-bold tracking-widest uppercase">
              OTHER KEY AREAS
            </span>
            <h2 className="mt-6 font-cormorant text-5xl md:text-6xl text-white">
              More Priorities
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {otherPolicies.map((policy, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg p-8 shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-center mb-6">
                  <span className="text-4xl mr-4">{policy.icon}</span>
                  <h3 className="font-cormorant text-3xl text-navy">
                    {policy.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {policy.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start">
                      <span className="text-gold mr-3 text-lg flex-shrink-0">●</span>
                      <span className="text-gray-600 text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-gold">
        <div className="container mx-auto text-center max-w-3xl">
          <h2 className="font-cormorant text-4xl md:text-5xl text-navy mb-6">
            Join the Fight for Life, Faith, Family & Freedom
          </h2>
          <p className="text-navy/80 text-lg mb-8 leading-relaxed">
            These policies represent my commitment to Western Australians. If you share these values, I would love your support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="bg-navy hover:bg-navy/90 text-white font-bold px-8 py-6 text-sm tracking-wide rounded-md transition-colors"
            >
              <Link to="/contact">GET INVOLVED</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-2 border-navy text-navy hover:bg-navy hover:text-white font-bold px-8 py-6 text-sm tracking-wide rounded-md transition-colors bg-white"
            >
              <Link to="/campaigns">VIEW CAMPAIGNS</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Policies;
